import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';

const AdminDashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
  });

  if (isLoading) return <div className="loading">Loading dashboard...</div>;
  if (error) return <div className="error">Error loading dashboard</div>;
  if (!stats) return <div>No data available</div>;

  const { overview, recentMoves, topStyles } = stats;

  return (
    <div className="admin-dashboard">
      <h1>Dashboard</h1>

      <div className="dashboard-stats-section">
        <h2 className="dashboard-section-header">Core Content</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Published Moves</h3>
            <div className="stat-number">{overview.publishedMoves}</div>
            <div className="stat-subtitle">of {overview.totalMoves} total</div>
          </div>
          <div className="stat-card">
            <h3>Unpublished Moves</h3>
            <div className="stat-number warning">{overview.unpublishedMoves}</div>
          </div>
          <div className="stat-card">
            <h3>Published Patterns</h3>
            <div className="stat-number">{overview.publishedPatterns}</div>
            <div className="stat-subtitle">of {overview.totalPatterns} total</div>
          </div>
          <div className="stat-card">
            <h3>Unpublished Patterns</h3>
            <div className="stat-number warning">{overview.unpublishedPatterns}</div>
          </div>
          <div className="stat-card">
            <h3>Breakdowns</h3>
            <div className="stat-number">{overview.publishedBreakdowns}</div>
            <div className="stat-subtitle">of {overview.totalBreakdowns} total ({overview.unpublishedBreakdowns} unpublished)</div>
          </div>
          <div className="stat-card">
            <h3>Move Demonstrations</h3>
            <div className="stat-number">{overview.approvedMoveDemos}</div>
            <div className="stat-subtitle">of {overview.moveDemonstrations} total ({overview.pendingMoveDemos} pending)</div>
          </div>
          <div className="stat-card">
            <h3>Pattern Demonstrations</h3>
            <div className="stat-number">{overview.approvedPatternDemos}</div>
            <div className="stat-subtitle">of {overview.patternDemonstrations} total ({overview.pendingPatternDemos} pending)</div>
          </div>
        </div>
      </div>

      <div className="dashboard-stats-section">
        <h2 className="dashboard-section-header">Reference Tables</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Dance Styles</h3>
            <div className="stat-number">{overview.totalStyles}</div>
          </div>
          <div className="stat-card">
            <h3>Skill Levels</h3>
            <div className="stat-number">{overview.totalSkillLevels}</div>
          </div>
          <div className="stat-card">
            <h3>Move Types</h3>
            <div className="stat-number">{overview.totalMoveTypes}</div>
          </div>
          <div className="stat-card">
            <h3>Dance Roles</h3>
            <div className="stat-number">{overview.totalDanceRoles}</div>
          </div>
          <div className="stat-card">
            <h3>Breakdown Angles</h3>
            <div className="stat-number">{overview.totalBreakdownAngles}</div>
          </div>
          <div className="stat-card">
            <h3>Clip Types</h3>
            <div className="stat-number">{overview.totalBreakdownClipTypes}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-stats-section">
        <h2 className="dashboard-section-header">Users & Identity</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Active Instructors</h3>
            <div className="stat-number">{overview.activeInstructors}</div>
            <div className="stat-subtitle">of {overview.totalInstructors} total ({overview.inactiveInstructors} inactive)</div>
          </div>
          <div className="stat-card">
            <h3>Active Dancers</h3>
            <div className="stat-number">{overview.activeDancers}</div>
            <div className="stat-subtitle">of {overview.totalDancers} total ({overview.inactiveDancers} inactive)</div>
          </div>
          <div className="stat-card">
            <h3>Tenant Members</h3>
            <div className="stat-number">{overview.totalMembers ?? overview.totalUsers}</div>
            {overview.totalMembers !== undefined && (
              <div className="stat-subtitle">
                {overview.ownerMembers} owner{overview.ownerMembers === 1 ? '' : 's'} ·{' '}
                {overview.instructorMembers} instructor{overview.instructorMembers === 1 ? '' : 's'} ·{' '}
                {overview.studentMembers} student{overview.studentMembers === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h2>Recent Moves</h2>
          {recentMoves.length === 0 ? (
            <p>No moves created yet</p>
          ) : (
            <div className="recent-items">
              {recentMoves.map((move) => (
                <Link key={move.id} to={`/admin/moves?edit=${move.id}`} className="recent-item recent-item-link">
                  <h4>{move.name || move.title}</h4>
                  <p>
                    {move.style?.name} • {move.skillLevel?.name} • {move.name || move.title} • {move.instructor?.name}
                  </p>
                  <span className={`status ${move.isPublished ? 'published' : 'draft'}`}>
                    {move.isPublished ? 'Published' : 'Draft'}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <h2>Top Styles</h2>
          {topStyles.length === 0 ? (
            <p>No styles created yet</p>
          ) : (
            <div className="top-styles">
              {topStyles.map((style) => (
                <div key={style.id} className="style-item">
                  <h4>{style.name}</h4>
                  <p>{style.movesCount} moves</p>
                  {style.description && <p className="description">{style.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
